export interface RendererCapabilities {
  webgl2: boolean;
  halfFloat: boolean;
  p3: boolean;
  offscreenCanvas: boolean;
}

export function detectRendererCapabilities(): RendererCapabilities {
  let webgl2 = false;
  let halfFloat = false;
  let p3 = false;

  try {
    const c = document.createElement('canvas');
    const gl = c.getContext('webgl2', { colorSpace: 'display-p3' }) as WebGL2RenderingContext | null;
    if (gl) {
      webgl2 = true;
      p3 = true;
      halfFloat = !!gl.getExtension('EXT_color_buffer_half_float');
    } else {
      const gl2 = c.getContext('webgl2') as WebGL2RenderingContext | null;
      if (gl2) {
        webgl2 = true;
        halfFloat = !!gl2.getExtension('EXT_color_buffer_half_float');
      }
    }
  } catch {
    // ignore
  }

  return {
    webgl2,
    halfFloat,
    p3,
    offscreenCanvas: typeof OffscreenCanvas !== 'undefined'
  };
}

export function isHighEndDevice(): boolean {
  const memory = (navigator as any).deviceMemory;
  if (memory && memory >= 6) return true;
  const hw = navigator.hardwareConcurrency;
  return hw >= 6;
}

function isIOSWebKit(): boolean {
  const platform = navigator.platform || '';
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function canUseWebGL2Renderer(): boolean {
  if (isIOSWebKit()) return false;
  const caps = detectRendererCapabilities();
  return caps.webgl2 && caps.offscreenCanvas && caps.halfFloat;
}

export function getOptimalResolution(sourceWidth: number, sourceHeight: number): { width: number; height: number } {
  const maxLongEdge = isHighEndDevice() ? 4096 : 2048;
  const longest = Math.max(sourceWidth, sourceHeight);
  if (longest <= maxLongEdge) {
    return { width: sourceWidth, height: sourceHeight };
  }
  const scale = maxLongEdge / longest;
  return {
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale))
  };
}
